import React, { useState } from 'react';
import { PlusIcon, SearchIcon, FilterIcon, CheckIcon, XIcon, EditIcon, DatabaseIcon, PackageIcon, ArchiveIcon, RefreshCwIcon, DownloadIcon, BuildingIcon, HomeIcon } from 'lucide-react';
import { Catalog, Entity, Site, Vendor } from '../CatalogsManagement';
import { CatalogCard } from './CatalogCard';
interface CatalogListProps {
  catalogs: Catalog[];
  vendors: Vendor[];
  entities: Entity[];
  sites: Site[];
  filters: {
    vendor: string;
    entity: string;
    site: string;
    status: string;
  };
  onFilterChange: (name: string, value: string) => void;
  onCreateCatalog: () => void;
  onEditCatalog: (catalog: Catalog) => void;
  onManageSchema: (catalog: Catalog) => void;
  onManageProducts: (catalog: Catalog) => void;
  onToggleStatus: (catalogId: string) => void;
  locale: string;
}
export function CatalogList({
  catalogs,
  vendors,
  entities,
  sites,
  filters,
  onFilterChange,
  onCreateCatalog,
  onEditCatalog,
  onManageSchema,
  onManageProducts,
  onToggleStatus,
  locale
}: CatalogListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  // Format date based on locale
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No End Date';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  // Filter catalogs by search term
  const filteredCatalogs = catalogs.filter(catalog => catalog.name.toLowerCase().includes(searchTerm.toLowerCase()) || catalog.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) || catalog.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Reset all filters
  const handleResetFilters = () => {
    onFilterChange('vendor', '');
    onFilterChange('entity', '');
    onFilterChange('site', '');
    onFilterChange('status', '');
    setSearchTerm('');
  };
  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  return <div className="space-y-6" data-id="element-11038">
      {/* Action bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0" data-id="element-11039">
        <div className="flex items-center space-x-2" data-id="element-11040">
          <div className="relative rounded-md shadow-sm" data-id="element-11041">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-11042">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-11043" />
            </div>
            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search catalogs..." value={searchTerm} onChange={handleSearch} data-id="element-11044" />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11045">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-11046" />
            Filters
            {(filters.vendor || filters.entity || filters.site || filters.status) && <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-800 rounded-full text-xs" data-id="element-11047">
                {[filters.vendor ? 1 : 0, filters.entity ? 1 : 0, filters.site ? 1 : 0, filters.status ? 1 : 0].reduce((a, b) => a + b, 0)}
              </span>}
          </button>
          <button onClick={handleResetFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11048">
            <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-11049" />
            Reset
          </button>
        </div>
        <div className="flex items-center space-x-2" data-id="element-11050">
          <div className="inline-flex shadow-sm rounded-md" data-id="element-11051">
            <button onClick={() => setViewMode('grid')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-l-md ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-11052">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-11053">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" data-id="element-11054" />
              </svg>
            </button>
            <button onClick={() => setViewMode('table')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-r-md ${viewMode === 'table' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-11055">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-11056">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" data-id="element-11057" />
              </svg>
            </button>
          </div>
          <button onClick={onCreateCatalog} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11058">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-11059" />
            Add Catalog
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && <div className="bg-gray-50 p-4 rounded-md shadow-sm border border-gray-200" data-id="element-11060">
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-4" data-id="element-11061">
            <div data-id="element-11062">
              <label htmlFor="vendor-filter" className="block text-sm font-medium text-gray-700" data-id="element-11063">
                Vendor
              </label>
              <select id="vendor-filter" name="vendor-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.vendor} onChange={e => onFilterChange('vendor', e.target.value)} data-id="element-11064">
                <option value="" data-id="element-11065">All Vendors</option>
                {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-11066">
                    {vendor.name}
                  </option>)}
              </select>
            </div>
            <div data-id="element-11067">
              <label htmlFor="entity-filter" className="block text-sm font-medium text-gray-700" data-id="element-11068">
                Entity
              </label>
              <select id="entity-filter" name="entity-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.entity} onChange={e => onFilterChange('entity', e.target.value)} data-id="element-11069">
                <option value="" data-id="element-11070">All Entities</option>
                {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-11071">
                    {entity.name}
                  </option>)}
              </select>
            </div>
            <div data-id="element-11072">
              <label htmlFor="site-filter" className="block text-sm font-medium text-gray-700" data-id="element-11073">
                Site
              </label>
              <select id="site-filter" name="site-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.site} onChange={e => onFilterChange('site', e.target.value)} data-id="element-11074">
                <option value="" data-id="element-11075">All Sites</option>
                {sites.map(site => <option key={site.id} value={site.id} data-id="element-11076">
                    {site.name}
                  </option>)}
              </select>
            </div>
            <div data-id="element-11077">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700" data-id="element-11078">
                Status
              </label>
              <select id="status-filter" name="status-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.status} onChange={e => onFilterChange('status', e.target.value)} data-id="element-11079">
                <option value="" data-id="element-11080">All Statuses</option>
                <option value="Active" data-id="element-11081">Active</option>
                <option value="Inactive" data-id="element-11082">Inactive</option>
                <option value="Archived" data-id="element-11083">Archived</option>
              </select>
            </div>
          </div>
        </div>}

      {/* Catalogs grid view */}
      {viewMode === 'grid' && <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-11084">
          {filteredCatalogs.length === 0 ? <div className="col-span-full text-center py-12 bg-white rounded-lg shadow" data-id="element-11085">
              <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center" data-id="element-11086">
                <PackageIcon className="h-12 w-12" data-id="element-11087" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-11088">
                No catalogs found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-11089">
                Get started by creating a new catalog.
              </p>
              <div className="mt-6" data-id="element-11090">
                <button onClick={onCreateCatalog} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11091">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-11092" />
                  Add Catalog
                </button>
              </div>
            </div> : filteredCatalogs.map(catalog => <CatalogCard key={catalog.id} catalog={catalog} onEdit={() => onEditCatalog(catalog)} onManageSchema={() => onManageSchema(catalog)} onManageProducts={() => onManageProducts(catalog)} onToggleStatus={() => onToggleStatus(catalog.id)} locale={locale} data-id="element-11093" />)}
        </div>}

      {/* Catalogs table view */}
      {viewMode === 'table' && <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-11094">
          {filteredCatalogs.length === 0 ? <div className="text-center py-12" data-id="element-11095">
              <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center" data-id="element-11096">
                <PackageIcon className="h-12 w-12" data-id="element-11097" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-11098">
                No catalogs found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-11099">
                Get started by creating a new catalog.
              </p>
              <div className="mt-6" data-id="element-11100">
                <button onClick={onCreateCatalog} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11101">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-11102" />
                  Add Catalog
                </button>
              </div>
            </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-11103">
              <thead className="bg-gray-50" data-id="element-11104">
                <tr data-id="element-11105">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11106">
                    Catalog
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11107">
                    Vendor
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11108">
                    Products
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11109">
                    Scope
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11110">
                    Active Period
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11111">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11112">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-11113">
                {filteredCatalogs.map(catalog => <tr key={catalog.id} data-id="element-11114">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-11115">
                      <div className="text-sm font-medium text-indigo-600" data-id="element-11116">
                        {catalog.name}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-11117">
                        {catalog.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-11118">
                      <div className="text-sm text-gray-900" data-id="element-11119">
                        {catalog.vendorName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-11120">
                      <div className="text-sm text-gray-900" data-id="element-11121">
                        {catalog.productCount}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-11122">
                      <div className="flex flex-col space-y-1" data-id="element-11123">
                        <div className="text-xs text-gray-500" data-id="element-11124">
                          <span className="inline-flex items-center" data-id="element-11125">
                            <BuildingIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-11126" />
                            {catalog.entities.length}{' '}
                            {catalog.entities.length === 1 ? 'Entity' : 'Entities'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-11127">
                          <span className="inline-flex items-center" data-id="element-11128">
                            <HomeIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-11129" />
                            {catalog.sites.length}{' '}
                            {catalog.sites.length === 1 ? 'Site' : 'Sites'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-11130">
                      <div className="text-sm text-gray-900" data-id="element-11131">
                        {formatDate(catalog.activeFrom)}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-11132">
                        to {formatDate(catalog.activeTo)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-11133">
                      {catalog.isActive ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-11134">
                          <CheckIcon className="h-4 w-4 mr-1" data-id="element-11135" />
                          Active
                        </span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-11136">
                          <XIcon className="h-4 w-4 mr-1" data-id="element-11137" />
                          Inactive
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11138">
                      <div className="flex justify-end space-x-2" data-id="element-11139">
                        <button onClick={() => onEditCatalog(catalog)} className="text-indigo-600 hover:text-indigo-900" title="Edit Catalog" data-id="element-11140">
                          <EditIcon className="h-4 w-4" data-id="element-11141" />
                        </button>
                        <button onClick={() => onManageSchema(catalog)} className="text-indigo-600 hover:text-indigo-900" title="Manage Schema" data-id="element-11142">
                          <DatabaseIcon className="h-4 w-4" data-id="element-11143" />
                        </button>
                        <button onClick={() => onManageProducts(catalog)} className="text-indigo-600 hover:text-indigo-900" title="Manage Products" data-id="element-11144">
                          <PackageIcon className="h-4 w-4" data-id="element-11145" />
                        </button>
                        <button onClick={() => onToggleStatus(catalog.id)} className={catalog.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} title={catalog.isActive ? 'Deactivate' : 'Activate'} data-id="element-11146">
                          {catalog.isActive ? <XIcon className="h-4 w-4" data-id="element-11147" /> : <CheckIcon className="h-4 w-4" data-id="element-11148" />}
                        </button>
                        <button className="text-gray-600 hover:text-gray-900" title="Export Products" data-id="element-11149">
                          <DownloadIcon className="h-4 w-4" data-id="element-11150" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>}
        </div>}
    </div>;
}